"use client";

import * as React from "react";

import {
  formatDateTime,
  formatDayMonthYear,
  formatExpiryDate,
  formatIssueDate,
  formatShortMonthDay,
} from "@/shared/lib/format/dates";

type Variant = "datetime" | "issue" | "expiry" | "dayMonthYear" | "shortMonthDay";

const FORMATTERS: Record<Variant, (value: string, locale: string) => string> = {
  datetime: (value, locale) => formatDateTime(value, locale),
  issue: (value, locale) => formatIssueDate(value, locale),
  expiry: (value, locale) => formatExpiryDate(value, locale),
  dayMonthYear: (value, locale) => formatDayMonthYear(value, locale),
  shortMonthDay: (value, locale) => formatShortMonthDay(value, locale),
};

type FormattedDateProps = Omit<React.TimeHTMLAttributes<HTMLTimeElement>, "dateTime"> & {
  value: string | number | null | undefined;
  locale: string;
  variant?: Variant;
  fallback?: React.ReactNode;
};

export function FormattedDate({
  value,
  locale,
  variant = "dayMonthYear",
  fallback = null,
  className,
  ...rest
}: FormattedDateProps) {
  if (value == null || value === "") return <>{fallback}</>;

  const date = new Date(typeof value === "number" ? value : String(value));
  if (Number.isNaN(date.getTime())) return <>{String(value)}</>;

  const formatted = FORMATTERS[variant](String(value), locale);
  if (!formatted || formatted === "-") return <>{fallback ?? formatted}</>;

  const includeTime = variant === "datetime";
  const iso = includeTime ? date.toISOString() : date.toISOString().slice(0, 10);

  return (
    <time dateTime={iso} className={className} {...rest}>
      {formatted}
    </time>
  );
}
